import simpleRestProvider from 'ra-data-simple-rest'
import { apiUrl, httpClient, TABLES_PATH } from './httpClient'
import { appendIndustryIdToUrl, getSelectedIndustryId, INDUSTRY_SCOPED_RESOURCES } from '../utils/industryUtils'
import { ERROR_AWARE_RESOURCES, INDUSTRY_SCOPED_GET_ONE_RESOURCES } from './constants/apiConstants'

const baseDataProvider = simpleRestProvider(`${apiUrl}${TABLES_PATH}`, httpClient)

const injectIndustryIdFilter = (params) => {
   const industryId = getSelectedIndustryId();
   if (!industryId) return params;

   return {
      ...params,
      filter: {
         ...(params.filter || {}),
         industry_id: industryId,
      },
   };
};

const injectIndustryIdData = (resource, params) => {
   if (!INDUSTRY_SCOPED_RESOURCES.includes(resource)) return params;

   const industryId = getSelectedIndustryId();
   if (!industryId) return params;

   return {
      ...params,
      data: {
         ...(params.data || {}),
         industry_id: industryId,
      },
   };
};

// httpClient resolves with the error object on 4xx/5xx, and the stock create/update would report success
const writeOrThrow = async (url, method, data) => {
   const response = await httpClient(url, { method, body: JSON.stringify(data) });
   if (!response || !response.json) throw new Error((response && response.message) || 'request failed');
   return { data: response.json };
};

const getOneWithIndustry = async (resource, params) => {
   const response = await httpClient(appendIndustryIdToUrl(`${apiUrl}${TABLES_PATH}/${resource}/${params.id}`));
   if (!response || !response.json) throw new Error((response && response.message) || 'request failed');
   return { data: response.json };
};

const getManyWithIndustry = (resource, params) => {
   const industryId = getSelectedIndustryId();
   const filter = JSON.stringify({ id: params.ids, industry_id: industryId });
   const url = `${apiUrl}${TABLES_PATH}/${resource}?filter=${encodeURIComponent(filter)}`;
   return httpClient(url).then(({ json }) => ({ data: json }));
};

// httpClient resolves with the error object on 4xx/5xx (no .json); the stock deleteMany would read
// json.id of undefined. Rejects like writeOrThrow, and carries the API's optional `warning` through.
const deleteManyWithWarnings = async (resource, params) => {
   const responses = await Promise.all(
      params.ids.map(id => httpClient(`${apiUrl}${TABLES_PATH}/${resource}/${id}`, { method: 'DELETE' }))
   );
   responses.forEach(response => {
      if (!response || !response.json) throw new Error((response && response.message) || 'request failed');
   });
   return {
      data: responses.map(({ json }) => json.id),
      warnings: responses.map(({ json }) => json.warning).filter(Boolean),
   };
};

export const dataProvider = {
   ...baseDataProvider,
   getList: (resource, params) => baseDataProvider.getList(resource, injectIndustryIdFilter(params)),
   getOne: (resource, params) => INDUSTRY_SCOPED_GET_ONE_RESOURCES.includes(resource)
      ? getOneWithIndustry(resource, params)
      : baseDataProvider.getOne(resource, params),
   getMany: getManyWithIndustry,
   getManyReference: (resource, params) => baseDataProvider.getManyReference(resource, injectIndustryIdFilter(params)),
   create: (resource, params) => ERROR_AWARE_RESOURCES.includes(resource)
      ? writeOrThrow(`${apiUrl}${TABLES_PATH}/${resource}`, 'POST', injectIndustryIdData(resource, params).data)
      : baseDataProvider.create(resource, injectIndustryIdData(resource, params)),
   update: (resource, params) => ERROR_AWARE_RESOURCES.includes(resource)
      ? writeOrThrow(`${apiUrl}${TABLES_PATH}/${resource}/${params.id}`, 'PUT', injectIndustryIdData(resource, params).data)
      : baseDataProvider.update(resource, injectIndustryIdData(resource, params)),
   updateMany: (resource, params) => baseDataProvider.updateMany(resource, injectIndustryIdData(resource, params)),
   // no industry injection here: DELETE carries no body, so httpClient puts industry_id in the query string
   delete: (resource, params) => baseDataProvider.delete(resource, params),
   deleteMany: deleteManyWithWarnings,
};
